import React, { useCallback, useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { Card, CardTitle, CardHeaderRow, Notice } from '../ui';
import {
    Muted, HistoFiltres, HistoSelect, HistoGrille, HistoCarte, HistoEntete,
    HistoSvg, HistoBulle, HistoAlertes, HistoAlerte,
    TuileTendance, TuilePied, TuileVariation,
} from './security.styled';
import { calculerEchelle, graduations, tracerCourbe, variation, tonTendance, MARGES } from './complianceChart';
import { getConformiteHistorique } from '../../api/internal/usersInternalApi';

const LARGEUR = 520;
const HAUTEUR = 150;

// L'aperçu de la tuile : pas d'axe, pas de graduation, pas d'étiquette. Il donne
// la FORME de l'évolution ; la mesure est dans le détail.
const APERCU = { largeur: 108, hauteur: 32 };

const PERIODES = [
    { valeur: 12, libelle: '12 semaines' },
    { valeur: 26, libelle: '6 mois' },
    { valeur: 52, libelle: '1 an' },
];

const RATTACHEMENTS = [
    { valeur: 'tous', libelle: 'Tous les postes' },
    { valeur: 'intune', libelle: 'Enrôlés dans Intune' },
    { valeur: 'domaine', libelle: 'Joints au domaine' },
];

function formaterSemaine(semaine) {
    const [annee, s] = String(semaine).split('-');
    return { court: s, complet: `semaine ${String(s).replace('S', '')} de ${annee}` };
}

/* ═══════════════════════ Chargement partagé ═══════════════════════ */

// Un seul chargement pour la tuile et pour le détail : les deux lisent la même
// série, et les filtres du détail doivent se refléter sur la tuile — sinon deux
// chiffres différents s'affichent pour le même périmètre.
export function usePostureHistorique() {
    const [donnees, setDonnees] = useState(null);
    const [chargement, setChargement] = useState(true);
    const [erreur, setErreur] = useState(null);
    const [filtres, setFiltres] = useState({ semaines: 12, rattachement: 'tous', etoile: '', familleOs: '' });

    const charger = useCallback(async (signal) => {
        setChargement(true);
        try {
            const reponse = await getConformiteHistorique(filtres, signal);
            if (reponse) { setDonnees(reponse); setErreur(null); }
        } catch (e) {
            setErreur(e?.response?.status === 401
                ? "Vous n'avez pas les droits pour consulter l'historique."
                : "Impossible de charger l'historique de conformité.");
        } finally {
            setChargement(false);
        }
    }, [filtres]);

    useEffect(() => {
        const controleur = new AbortController();
        charger(controleur.signal);
        return () => controleur.abort();
    }, [charger]);

    return { donnees, chargement, erreur, filtres, setFiltres };
}

// Série exploitable par les courbes, à partir de la réponse de l'API.
function serie(donnees, champ) {
    return (donnees?.semaines || []).map((s) => ({ semaine: s.semaine, jour: s.jour, valeur: s[champ] }));
}

/* ═══════════════════════ Tuile ═══════════════════════ */

// `sensFavorable` dit dans quel sens va le mieux : +1 pour un taux de conformité
// qui monte, -1 pour un score d'exposition qui baisse. Sans lui, la couleur
// dirait « vert » à une exposition qui se dégrade.
// `valeurCourante` prend le pas sur le dernier point de la série quand une
// mesure plus fraîche existe : le score d'exposition est lu en direct chez
// Defender (cache de 30 min), alors que les instantanés sont quotidiens. La
// tuile affiche donc la valeur du moment, et la tendance vient de l'historique.
export function TuilePosture({
    titre, donnees, champ, formater, sensFavorable, unite,
    ouvert, onBasculer, sousTitre, valeurCourante,
}) {
    const theme = useTheme();
    const points = serie(donnees, champ);
    const connus = points.filter((p) => Number.isFinite(p.valeur));
    const dernierPoint = connus[connus.length - 1] || null;
    const affichee = Number.isFinite(valeurCourante)
        ? valeurCourante
        : (dernierPoint ? dernierPoint.valeur : null);
    const evolution = variation(points);

    const ton = tonTendance(evolution, sensFavorable);
    const couleur = theme[ton] || theme.accent;

    const echelle = calculerEchelle(points.map((p) => p.valeur));
    const apercu = tracerCourbe(points, echelle, {
        largeur: APERCU.largeur + MARGES.gauche + MARGES.droite,
        hauteur: APERCU.hauteur + MARGES.haut + MARGES.bas,
    });
    const dernierMarqueur = apercu.marqueurs.filter((m) => m.connu).at(-1);

    return (
        <TuileTendance type="button" $tone={ton} $ouvert={ouvert} onClick={onBasculer}
            aria-expanded={ouvert} title={ouvert ? 'Replier le détail' : 'Voir la courbe et les filtres'}>
            <div className="label">
                <span>{titre}</span>
                <i className={`bx bx-chevron-${ouvert ? 'up' : 'down'}`} />
            </div>
            <div className="value">{affichee === null ? '—' : formater(affichee)}</div>
            <div className="hint">{sousTitre}</div>

            <TuilePied>
                {evolution ? (
                    <TuileVariation $tone={ton}>
                        <i className={`bx bx-trending-${evolution.ecart === 0 ? 'flat' : evolution.ecart > 0 ? 'up' : 'down'}`} />
                        {evolution.ecart > 0 ? '+' : ''}{Math.round(evolution.ecart * 10) / 10}{unite}
                        <Muted style={{ fontWeight: 400 }}>depuis {formaterSemaine(evolution.depuis).court}</Muted>
                    </TuileVariation>
                ) : (
                    <Muted style={{ fontSize: '0.72rem' }}>tendance dès le 2ᵉ relevé</Muted>
                )}

                {/* L'aperçu n'est rendu qu'à partir de deux points : une courbe
                    d'un seul point est un pixel, qui ne dit rien. */}
                {connus.length > 1 && (
                    <svg width={APERCU.largeur} height={APERCU.hauteur}
                        viewBox={`${MARGES.gauche} ${MARGES.haut} ${APERCU.largeur} ${APERCU.hauteur}`}
                        aria-hidden="true" focusable="false">
                        <path d={apercu.aire} fill={couleur} fillOpacity="0.16" stroke="none" />
                        <path d={apercu.ligne} fill="none" stroke={couleur} strokeWidth="1.6"
                            strokeLinejoin="round" strokeLinecap="round" />
                        {dernierMarqueur && (
                            <circle cx={dernierMarqueur.x} cy={dernierMarqueur.y} r="2.6"
                                fill={couleur} stroke={theme.surface} strokeWidth="1.4" />
                        )}
                    </svg>
                )}
            </TuilePied>
        </TuileTendance>
    );
}

/* ═══════════════════════ Courbe du détail ═══════════════════════ */

// Deux mesures d'unités différentes ne partagent JAMAIS un axe : un taux où haut
// = bon et un score où haut = mauvais superposés produiraient un graphique qui
// mentirait sur la corrélation.
function Courbe({ titre, sousTitre, points, tone, formater, borner }) {
    const [survol, setSurvol] = useState(null);
    // Les attributs SVG ne lisent pas le thème styled-components : la couleur
    // doit être résolue en JavaScript, sinon le tracé garderait les teintes du
    // thème clair en mode sombre.
    const theme = useTheme();
    const couleur = theme[tone] || theme.accent;

    let echelle = calculerEchelle(points.map((p) => p.valeur));
    if (echelle && borner) {
        echelle = { min: Math.max(borner[0], echelle.min), max: Math.min(borner[1], echelle.max) };
    }

    const { ligne, aire, marqueurs } = tracerCourbe(points, echelle, { largeur: LARGEUR, hauteur: HAUTEUR });
    const connus = marqueurs.filter((m) => m.connu);
    const dernier = connus[connus.length - 1] || null;
    const basCadre = HAUTEUR - MARGES.bas;

    // Trois étiquettes de semaine au maximum : à douze points, une étiquette par
    // semaine se chevauche.
    const indicesEtiquettes = marqueurs.length <= 3
        ? marqueurs.map((_, i) => i)
        : [0, Math.floor((marqueurs.length - 1) / 2), marqueurs.length - 1];

    return (
        <HistoCarte>
            <HistoEntete>
                <div>
                    <div className="titre">{titre}</div>
                    <Muted style={{ fontSize: '0.78rem' }}>{sousTitre}</Muted>
                </div>
                <div className="valeur">{dernier ? formater(dernier.valeur) : '—'}</div>
            </HistoEntete>

            {survol && (
                <HistoBulle style={{ left: `${(survol.x / LARGEUR) * 100}%`, top: '4px' }}>
                    <div>{formaterSemaine(survol.semaine).complet}</div>
                    <strong>{formater(survol.valeur)}</strong>
                    <Muted style={{ fontSize: '0.72rem' }}> · relevé le {new Date(survol.jour).toLocaleDateString('fr-FR')}</Muted>
                </HistoBulle>
            )}

            <HistoSvg viewBox={`0 0 ${LARGEUR} ${HAUTEUR}`} role="img"
                aria-label={`${titre} : ${connus.map((m) => `${formaterSemaine(m.semaine).court} ${formater(m.valeur)}`).join(', ')}`}>

                {/* Graduations : chacune nomme une valeur atteinte par la fenêtre */}
                {graduations(echelle, formater).map((g) => {
                    const y = MARGES.haut + (HAUTEUR - MARGES.haut - MARGES.bas) * g.part;
                    return (
                        <g key={g.valeur}>
                            <line x1={MARGES.gauche} y1={y} x2={LARGEUR - MARGES.droite} y2={y}
                                stroke="currentColor" strokeWidth="1" opacity="0.14" />
                            <text x={MARGES.gauche - 6} y={y + 3} textAnchor="end"
                                fontSize="9" fill="currentColor" opacity="0.55">{g.libelle}</text>
                        </g>
                    );
                })}

                {aire && <path d={aire} fill={couleur} fillOpacity="0.14" stroke="none" />}
                {ligne && <path d={ligne} fill="none" stroke={couleur} strokeWidth="2"
                    strokeLinejoin="round" strokeLinecap="round" />}

                {connus.map((m, i) => (
                    <circle key={m.semaine} cx={m.x} cy={m.y}
                        r={i === connus.length - 1 ? 4.5 : 3}
                        fill={couleur} stroke={theme.surface2} strokeWidth="2" />
                ))}

                {/* Cibles de survol plus larges que les marqueurs */}
                {marqueurs.map((m) => (
                    <rect key={`cible-${m.semaine}`} x={m.x - 14} y={MARGES.haut - 8}
                        width="28" height={basCadre - MARGES.haut + 16}
                        fill="transparent" style={{ cursor: m.connu ? 'pointer' : 'default' }}
                        onMouseEnter={() => m.connu && setSurvol(m)}
                        onMouseLeave={() => setSurvol(null)} />
                ))}

                {marqueurs.map((m, i) => indicesEtiquettes.includes(i) && (
                    <text key={`etiquette-${m.semaine}`} x={m.x} y={HAUTEUR - 8}
                        textAnchor={i === 0 ? 'start' : i === marqueurs.length - 1 ? 'end' : 'middle'}
                        fontSize="9" fill="currentColor" opacity="0.55">
                        {formaterSemaine(m.semaine).court}
                    </text>
                ))}
            </HistoSvg>
        </HistoCarte>
    );
}

/* ═══════════════════════ Détail dépliable ═══════════════════════ */

export default function DetailConformite({ donnees, chargement, erreur, filtres, setFiltres, focus }) {
    const modifier = (cle) => (evenement) => setFiltres((p) => ({ ...p, [cle]: evenement.target.value }));
    const surConformite = focus !== 'exposition';
    const semaines = donnees?.semaines || [];
    const dernier = semaines[semaines.length - 1] || null;

    // UNE seule courbe : celle de la tuile cliquée. Afficher l'autre à côté
    // invite à comparer deux mesures qui n'ont ni la même unité, ni le même
    // périmètre — le score d'exposition est global au tenant, la conformité se
    // filtre par étoile.
    const courbes = (dernier ? [
        {
            cle: 'conformite',
            titre: 'Conformité des postes évalués',
            sousTitre: `${dernier.conformes} conformes sur ${dernier.conformes + dernier.nonConformes} évalués · ${dernier.nonEvalues} non évalués`,
            points: serie(donnees, 'tauxSurEvalues'),
            tone: 'ok',
            borner: [0, 100],
            formater: (v) => `${Math.round(v * 10) / 10} %`,
        },
        {
            cle: 'exposition',
            titre: "Score d'exposition Defender",
            sousTitre: 'plus bas est meilleur · à l\'échelle du tenant, non filtrable',
            points: serie(donnees, 'exposureScore'),
            tone: 'danger',
            formater: (v) => `${Math.round(v * 10) / 10}`,
        },
    ] : []).filter((c) => c.cle === focus);

    return (
        <Card>
            <CardHeaderRow>
                <CardTitle>
                    {surConformite ? 'Historique de conformité' : "Historique du score d'exposition"}
                    <Muted style={{ fontWeight: 400, fontSize: '0.78rem', marginLeft: '8px' }}>
                        un point par semaine, sur le dernier relevé de chacune
                    </Muted>
                </CardTitle>
            </CardHeaderRow>

            <HistoFiltres>
                <HistoSelect value={filtres.semaines} onChange={modifier('semaines')} aria-label="Période">
                    {PERIODES.map((p) => <option key={p.valeur} value={p.valeur}>{p.libelle}</option>)}
                </HistoSelect>

                {/* Les filtres de périmètre n'existent que pour la conformité :
                    le score d'exposition est calculé par Defender à l'échelle du
                    tenant. Les afficher ici laisserait croire qu'ils agissent. */}
                {surConformite && (
                    <>
                        <HistoSelect value={filtres.rattachement} onChange={modifier('rattachement')} aria-label="Rattachement">
                            {RATTACHEMENTS.map((r) => <option key={r.valeur} value={r.valeur}>{r.libelle}</option>)}
                        </HistoSelect>
                        <HistoSelect value={filtres.etoile} onChange={modifier('etoile')} aria-label="Étoile">
                            <option value="">Toutes les étoiles</option>
                            {(donnees?.dimensions?.etoiles || []).map((e) => (
                                <option key={e || 'sans'} value={e || ''}>{e || 'Sans propriétaire'}</option>
                            ))}
                        </HistoSelect>
                        <HistoSelect value={filtres.familleOs} onChange={modifier('familleOs')} aria-label="Système">
                            <option value="">Tous les systèmes</option>
                            {(donnees?.dimensions?.famillesOs || []).map((f) => <option key={f} value={f}>{f}</option>)}
                        </HistoSelect>
                    </>
                )}
                {chargement && <Muted style={{ fontSize: '0.78rem' }}>chargement…</Muted>}
            </HistoFiltres>

            {erreur && <Notice $tone="danger"><i className="bx bx-error-circle" />{erreur}</Notice>}

            {!erreur && semaines.length === 0 && (
                <Notice $tone="accent">
                    <i className="bx bx-history" />
                    {donnees?.message
                        || "Aucun relevé sur la période. L'historique se constitue à partir du premier passage de la tâche « Instantané de conformité »."}
                </Notice>
            )}

            {!erreur && semaines.length === 1 && (
                <Notice $tone="warn">
                    <i className="bx bx-info-circle" />
                    Un seul relevé disponible ({semaines[0].jour}) : la courbe demande au moins deux semaines.
                    Les valeurs ci-dessous sont l'état du jour.
                </Notice>
            )}

            {!erreur && semaines.length > 0 && (
                <>
                    <HistoGrille>
                        {courbes.map((c) => <Courbe key={c.cle} {...c} />)}
                    </HistoGrille>

                    {/* L'alerte : ce que le détail paramètre par paramètre dit du
                        verdict d'Entra. Les deux premiers compteurs sont des
                        désaccords, les autres des angles morts. Rien à voir avec
                        le score d'exposition, d'où la condition. */}
                    {surConformite && (
                    <HistoAlertes>
                        <HistoAlerte $tone={dernier.conformeMaisParametreEnEchec > 0 ? 'danger' : 'neutre'}>
                            <div className="valeur">{dernier.conformeMaisParametreEnEchec}</div>
                            <div className="libelle">déclarés conformes alors qu'un paramètre échoue</div>
                        </HistoAlerte>
                        <HistoAlerte $tone={dernier.nonConformeMaisAucunEchec > 0 ? 'warn' : 'neutre'}>
                            <div className="valeur">{dernier.nonConformeMaisAucunEchec}</div>
                            <div className="libelle">déclarés non conformes sans aucun paramètre en échec</div>
                        </HistoAlerte>
                        <HistoAlerte $tone="neutre">
                            <div className="valeur">{dernier.avecParametreNonConforme}</div>
                            <div className="libelle">postes avec un paramètre non conforme</div>
                        </HistoAlerte>
                        <HistoAlerte $tone="neutre">
                            <div className="valeur">{dernier.avecParametreEnErreur}</div>
                            <div className="libelle">postes dont l'évaluation a échoué — mesure manquante, pas un défaut</div>
                        </HistoAlerte>
                        <HistoAlerte $tone="neutre">
                            <div className="valeur">{dernier.sansDetail}</div>
                            <div className="libelle">postes Intune sans aucun paramètre collecté</div>
                        </HistoAlerte>
                    </HistoAlertes>
                    )}

                    {donnees.dernierePrise && (
                        <Muted style={{ display: 'block', marginTop: '10px', fontSize: '0.72rem' }}>
                            Dernier relevé le {new Date(donnees.dernierePrise).toLocaleString('fr-FR')}.
                            {surConformite
                                ? " Le verdict affiché est celui d'Entra ; les compteurs ci-dessus disent ce que le détail des paramètres en pense."
                                : " Score calculé par Defender à l'échelle du tenant : il ne se filtre ni par étoile, ni par rattachement."}
                        </Muted>
                    )}
                </>
            )}
        </Card>
    );
}
